import React, { useState, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight, FileText } from 'lucide-react';

interface TimelineCalendarProps {
  timelines: any[];
  publications: any[];
  onClose: () => void;
}

// Map of the 6-step timeline steps to colors (for Data Analysis and Manuscript stages)
const STEP_COLORS: { [key: string]: string } = {
  'Main messages, figures, and tables': 'bg-blue-500',
  'Draft completed': 'bg-green-500',
  'Internal review': 'bg-yellow-500',
  'External review': 'bg-orange-500',
  'Submission': 'bg-red-500',
  'Revision': 'bg-purple-500',
};

const STEP_NAMES = [
  'Main messages, figures, and tables',
  'Draft completed',
  'Internal review',
  'External review',
  'Submission',
  'Revision',
];

const TimelineCalendar: React.FC<TimelineCalendarProps> = ({
  timelines,
  publications,
  onClose,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedTimeline, setSelectedTimeline] = useState<any | null>(null);
  const [selectedTimelinesForStep, setSelectedTimelinesForStep] = useState<{ stepTitle: string; timelines: any[] } | null>(null);
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'agenda'>('month');
  const [agendaSort, setAgendaSort] = useState<{
    key: 'date' | 'event' | 'paper';
    direction: 'asc' | 'desc';
  }>({ key: 'date', direction: 'asc' });

  // Filter timelines for Data Analysis (stage 3) and Manuscript (stage 4) stages
  const filteredTimelines = useMemo(() => {
    return timelines.filter(timeline => timeline.publication_stage === '3' || timeline.publication_stage === '4');
  }, [timelines]);

  // Get the first day of the current month
  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  // Get the last day of the current month
  const getLastDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  };

  // Get the first day (Sunday) of the current week
  const getWeekStart = (date: Date) => {
    const d = new Date(date);
    const day = d.getDay();
    d.setDate(d.getDate() - day);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // Get calendar days for full month view (including previous/next month days to fill the grid)
  const getCalendarDays = () => {
    const firstDay = getFirstDayOfMonth(currentDate);
    const lastDay = getLastDayOfMonth(currentDate);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay()); // Start from Sunday

    const days = [];
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay())); // End on Saturday

    const current = new Date(startDate);
    while (current <= endDate) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  // Get calendar days for week view (current week only)
  const getWeekDays = () => {
    const startOfWeek = getWeekStart(currentDate);
    const days: Date[] = [];
    const current = new Date(startOfWeek);

    for (let i = 0; i < 7; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  // Get timelines for a specific date
  const getTimelinesForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return filteredTimelines.filter(timeline => {
      const timelineDate = timeline.due_date || timeline.created_at;
      if (!timelineDate) return false;
      const timelineDateStr = new Date(timelineDate).toISOString().split('T')[0];
      return timelineDateStr === dateStr;
    });
  };

  // Agenda rows for current month (date + timelines)
  const agendaRows = useMemo(() => {
    const monthStart = getFirstDayOfMonth(currentDate);
    const monthEnd = getLastDayOfMonth(currentDate);

    // Group manuscript timelines by date within the current month
    const groups: { [dateKey: string]: { date: Date; items: any[] } } = {};

    filteredTimelines.forEach(timeline => {
      const timelineDateRaw = timeline.due_date || timeline.created_at;
      if (!timelineDateRaw) return;
      const d = new Date(timelineDateRaw);
      if (d < monthStart || d > monthEnd) return;

      const key = d.toISOString().split('T')[0];
      if (!groups[key]) {
        groups[key] = { date: new Date(d.getFullYear(), d.getMonth(), d.getDate()), items: [] };
      }
      groups[key].items.push(timeline);
    });

    // Flatten and sort by date then time
    const rows: { date: Date; item: any }[] = [];
    Object.values(groups).forEach(group => {
      group.items.forEach(item => {
        rows.push({ date: group.date, item });
      });
    });

    rows.sort((a, b) => {
      const dateDiff = a.date.getTime() - b.date.getTime();
      if (dateDiff !== 0) return dateDiff;

      const aRaw = a.item.due_date || a.item.created_at;
      const bRaw = b.item.due_date || b.item.created_at;
      if (!aRaw || !bRaw) return 0;
      return new Date(aRaw).getTime() - new Date(bRaw).getTime();
    });

    return rows;
  }, [currentDate, filteredTimelines]);

  const sortedAgendaRows = useMemo(() => {
    // Keep stable ordering by using original index as a final tie-breaker
    const rowsWithIndex = agendaRows.map((row, idx) => ({ row, idx }));

    const compareString = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });

    const getPaperTitle = (row: { date: Date; item: any }) => {
      const publication = publications.find((p: any) => p.id === row.item.publication_id);
      return (row.item.publication_title || publication?.title || 'Unknown Paper') as string;
    };

    rowsWithIndex.sort((a, b) => {
      const dir = agendaSort.direction === 'asc' ? 1 : -1;

      if (agendaSort.key === 'date') {
        const diff = a.row.date.getTime() - b.row.date.getTime();
        if (diff !== 0) return diff * dir;
      } else if (agendaSort.key === 'event') {
        const aEvent = String(a.row.item?.title || '');
        const bEvent = String(b.row.item?.title || '');
        const diff = compareString(aEvent, bEvent);
        if (diff !== 0) return diff * dir;
      } else if (agendaSort.key === 'paper') {
        const aPaper = String(getPaperTitle(a.row));
        const bPaper = String(getPaperTitle(b.row));
        const diff = compareString(aPaper, bPaper);
        if (diff !== 0) return diff * dir;
      }

      // Tie-breaker: preserve original order
      return a.idx - b.idx;
    });

    return rowsWithIndex.map(x => x.row);
  }, [agendaRows, agendaSort.direction, agendaSort.key, publications]);

  const toggleAgendaSort = (key: 'date' | 'event' | 'paper') => {
    setAgendaSort(prev => {
      if (prev.key !== key) return { key, direction: 'asc' };
      return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
    });
  };

  const agendaSortIndicator = (key: 'date' | 'event' | 'paper') => {
    if (agendaSort.key !== key) return null;
    return <span className="ml-1 text-xs text-gray-500">{agendaSort.direction === 'asc' ? '▲' : '▼'}</span>;
  };

  // Navigate to previous period (month or week depending on view)
  const goToPreviousMonth = () => {
    if (viewMode === 'week') {
      // Go back one week
      const d = new Date(currentDate);
      d.setDate(d.getDate() - 7);
      setCurrentDate(d);
    } else {
      // Month / Agenda use month navigation
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    }
  };

  // Navigate to next period (month or week depending on view)
  const goToNextMonth = () => {
    if (viewMode === 'week') {
      const d = new Date(currentDate);
      d.setDate(d.getDate() + 7);
      setCurrentDate(d);
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    }
  };

  // Navigate to today
  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const calendarDays = getCalendarDays();
  const weekDaysForView = getWeekDays();

  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const weekRangeLabel = (() => {
    const start = getWeekStart(currentDate);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);

    const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startStr} – ${endStr}`;
  })();

  const agendaRangeLabel = (() => {
    const start = getFirstDayOfMonth(currentDate);
    const end = getLastDayOfMonth(currentDate);
    const startStr = start.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    const endStr = end.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    return `${startStr} – ${endStr}`;
  })();

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-bold text-gray-900">Timeline Calendar</h2>
            <span className="text-sm text-gray-500">Data Analysis & Manuscript Stages</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={goToToday}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Today
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Calendar Navigation + View Mode */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <button
              onClick={goToPreviousMonth}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <h3 className="text-lg font-semibold text-gray-900">
              {viewMode === 'week' ? weekRangeLabel : viewMode === 'agenda' ? agendaRangeLabel : monthName}
            </h3>
            <button
              onClick={goToNextMonth}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('month')}
              className={`px-3 py-1.5 text-sm rounded-lg border ${
                viewMode === 'month'
                  ? 'bg-green-600 text-white border-green-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Month
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`px-3 py-1.5 text-sm rounded-lg border ${
                viewMode === 'week'
                  ? 'bg-green-600 text-white border-green-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setViewMode('agenda')}
              className={`px-3 py-1.5 text-sm rounded-lg border ${
                viewMode === 'agenda'
                  ? 'bg-green-600 text-white border-green-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Agenda
            </button>
          </div>
        </div>

        {/* Calendar / Week / Agenda Content */}
        <div className="flex-1 overflow-auto p-4">
          {viewMode === 'agenda' ? (
            // Agenda table for the month
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-100 border-b border-gray-200 text-sm font-semibold text-gray-700">
                    <th className="px-3 py-2 text-left border-r border-gray-200 w-28">
                      <button
                        type="button"
                        onClick={() => toggleAgendaSort('date')}
                        className="inline-flex items-center hover:text-gray-900"
                        title="Sort by date"
                      >
                        Date{agendaSortIndicator('date')}
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left border-r border-gray-200 w-56">
                      <button
                        type="button"
                        onClick={() => toggleAgendaSort('event')}
                        className="inline-flex items-center hover:text-gray-900"
                        title="Sort by event title (A–Z / Z–A)"
                      >
                        Event{agendaSortIndicator('event')}
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left">
                      <button
                        type="button"
                        onClick={() => toggleAgendaSort('paper')}
                        className="inline-flex items-center hover:text-gray-900"
                        title="Sort by paper title (A–Z / Z–A)"
                      >
                        Paper / Lead Author{agendaSortIndicator('paper')}
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAgendaRows.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-4 py-6 text-center text-gray-500 text-sm">
                        No Data Analysis or Manuscript stage timeline items found for this month.
                      </td>
                    </tr>
                  ) : (
                    sortedAgendaRows.map((row, index) => {
                      const dateStr = row.date.toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                      });

                      const stepColor = STEP_COLORS[row.item.title] || 'bg-gray-500';
                      
                      // Get publication info
                      const publication = publications.find((p: any) => p.id === row.item.publication_id);
                      const paperTitle = row.item.publication_title || publication?.title || 'Unknown Paper';
                      const leadAuthor = publication?.lead_author_name || publication?.lead_author_email || 'Unknown Author';

                      return (
                        <tr
                          key={`${row.item.id}-${index}`}
                          className="border-t border-gray-200 text-sm hover:bg-gray-50 cursor-pointer"
                          onClick={() => setSelectedTimeline(row.item)}
                        >
                          <td className="px-3 py-2 border-r border-gray-200 align-top whitespace-nowrap">
                            {dateStr}
                          </td>
                          <td className="px-3 py-2 border-r border-gray-200 align-top">
                            <div className="flex items-center space-x-2">
                              <span className={`inline-block w-3 h-3 rounded-full flex-shrink-0 ${stepColor}`} />
                              <span className="text-gray-900">{row.item.title}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 align-top">
                            <div className="text-gray-900 font-medium" title={paperTitle}>{paperTitle}</div>
                            <div className="text-gray-500 text-xs mt-0.5">{leadAuthor}</div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <>
              {/* Week day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {weekDays.map(day => (
                  <div key={day} className="text-center text-sm font-medium text-gray-600 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar days for month or week */}
              <div className="grid grid-cols-7 gap-1">
                {(viewMode === 'week' ? weekDaysForView : calendarDays).map((day, index) => {
                  const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                  const isToday = day.toDateString() === new Date().toDateString();
                  const dayTimelines = getTimelinesForDate(day);

                  return (
                    <div
                      key={index}
                      className={`min-h-[90px] border border-gray-200 rounded-lg p-2 ${
                        viewMode === 'week'
                          ? 'bg-white'
                          : !isCurrentMonth
                          ? 'bg-gray-50'
                          : 'bg-white'
                      } ${isToday ? 'ring-2 ring-green-500' : ''}`}
                    >
                      <div
                        className={`text-sm font-medium mb-1 ${
                          !isCurrentMonth && viewMode !== 'week'
                            ? 'text-gray-400'
                            : isToday
                            ? 'text-green-600'
                            : 'text-gray-700'
                        }`}
                      >
                        {day.getDate()}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(() => {
                          // Group timelines by step type (title) and count them
                          const groupedByStep: { [key: string]: any[] } = {};
                          dayTimelines.forEach(timeline => {
                            const stepTitle = timeline.title || 'Unknown';
                            if (!groupedByStep[stepTitle]) {
                              groupedByStep[stepTitle] = [];
                            }
                            groupedByStep[stepTitle].push(timeline);
                          });

                          // Render one circle per step type with count
                          return Object.entries(groupedByStep).map(([stepTitle, timelines]) => {
                            const stepColor = STEP_COLORS[stepTitle] || 'bg-gray-500';
                            const count = timelines.length;
                            const displayCount = count > 1 ? count : '';

                            return (
                              <button
                                key={stepTitle}
                                onClick={() => {
                                  // If multiple timelines of same type, show selection list
                                  if (count > 1) {
                                    setSelectedTimelinesForStep({ stepTitle, timelines });
                                  } else {
                                    setSelectedTimeline(timelines[0]);
                                  }
                                }}
                                className={`w-6 h-6 rounded-full ${stepColor} hover:opacity-80 transition-opacity cursor-pointer flex items-center justify-center text-white text-xs font-semibold`}
                                title={`${stepTitle}${count > 1 ? ` (${count} items)` : ''}`}
                              >
                                {displayCount}
                              </button>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Legend */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="flex items-center space-x-6 flex-wrap gap-2">
            <span className="text-sm font-medium text-gray-700">Legend:</span>
            {STEP_NAMES.map(stepName => (
              <div key={stepName} className="flex items-center space-x-2">
                <div className={`w-4 h-4 rounded-full ${STEP_COLORS[stepName]}`} />
                <span className="text-xs text-gray-600">{stepName}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline Detail Modal */}
        {selectedTimeline && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-xl font-bold text-gray-900">Timeline Details</h3>
                <button
                  onClick={() => setSelectedTimeline(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Title</label>
                  <p className="mt-1 text-gray-900">{selectedTimeline.title}</p>
                </div>
                {selectedTimeline.publication_title && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Publication</label>
                    <div className="mt-1 flex items-center space-x-2">
                      <FileText className="h-4 w-4 text-gray-400" />
                      <p className="text-gray-900">{selectedTimeline.publication_title}</p>
                    </div>
                  </div>
                )}
                {selectedTimeline.description && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Description</label>
                    <p className="mt-1 text-gray-900">{selectedTimeline.description}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  {selectedTimeline.due_date && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Due Date</label>
                      <p className="mt-1 text-gray-900">
                        {new Date(selectedTimeline.due_date).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  {selectedTimeline.completed_date && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Completed Date</label>
                      <p className="mt-1 text-gray-900">
                        {new Date(selectedTimeline.completed_date).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Status</label>
                  <p className="mt-1">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      selectedTimeline.status === 'completed' ? 'bg-green-100 text-green-800' :
                      selectedTimeline.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                      selectedTimeline.status === 'overdue' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedTimeline.status?.replace('_', ' ') || 'pending'}
                    </span>
                  </p>
                </div>
                {selectedTimeline.created_by_name && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Created By</label>
                    <p className="mt-1 text-gray-900">{selectedTimeline.created_by_name}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-700">Created At</label>
                  <p className="mt-1 text-gray-900">
                    {new Date(selectedTimeline.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Multiple Timelines Selection Modal */}
        {selectedTimelinesForStep && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-xl font-bold text-gray-900">
                  Select Timeline: {selectedTimelinesForStep.stepTitle}
                </h3>
                <button
                  onClick={() => setSelectedTimelinesForStep(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-6">
                <p className="text-sm text-gray-600 mb-4">
                  {selectedTimelinesForStep.timelines.length} timeline item(s) found for this step:
                </p>
                <div className="space-y-2">
                  {selectedTimelinesForStep.timelines.map((timeline, index) => (
                    <button
                      key={timeline.id}
                      onClick={() => {
                        setSelectedTimeline(timeline);
                        setSelectedTimelinesForStep(null);
                      }}
                      className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-green-500 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <FileText className="h-4 w-4 text-gray-400" />
                            <span className="font-medium text-gray-900">
                              {timeline.publication_title || `Publication #${timeline.publication_id}`}
                            </span>
                          </div>
                          {timeline.description && (
                            <p className="text-sm text-gray-600 mb-2">{timeline.description}</p>
                          )}
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            {timeline.due_date && (
                              <span>Due: {new Date(timeline.due_date).toLocaleDateString()}</span>
                            )}
                            {timeline.completed_date && (
                              <span>Completed: {new Date(timeline.completed_date).toLocaleDateString()}</span>
                            )}
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              timeline.status === 'completed' ? 'bg-green-100 text-green-800' :
                              timeline.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                              timeline.status === 'overdue' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {timeline.status?.replace('_', ' ') || 'pending'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimelineCalendar;
